import './SideBySideCalculatorStyle.less';

import { CurrencyInput } from '@opendoor/bricks';
import { mapValues } from 'lodash';
import React from 'react';

import { Omit } from '../../declarations';
import { centsToCurrencyString } from '../../helpers/formatUtils';
import {
  FieldLabel,
  HeaderColumn,
  IHeaderColumnProps,
  IPerkListColumnProps,
  ITextColumnProps,
  PerkListColumn,
  SubfieldLabel,
  TextColumn,
} from './CalculatorCells';
import PercentCurrencyInput, { IPercentCurrencyInputProps } from './PercentCurrencyInput';
import SideBySide, {
  ISideBySideBaseField,
  ISideBySideColumn,
  ISideBySideField,
  ISideBySideSubField,
} from './SideBySide';

interface ISBSCalculatorTextColumn {
  config: ITextColumnProps;
  type: 'TEXT';
}

interface ISBSCalculatorHeaderColumn {
  config: IHeaderColumnProps;
  type: 'HEADER';
}

interface ISBSCalculatorEditable {
  isEditable?: boolean;
  onUpdateKey?: string;
}

interface ISBSCalculatorCurrencyColumn extends ISBSCalculatorEditable {
  config?: { bolder?: boolean; placeholder?: string; value?: number };
  type: 'CURRENCY';
}

interface ISBSCalculatorCurrencyPercentColumn extends ISBSCalculatorEditable {
  config: Omit<IPercentCurrencyInputProps, 'name'>;
  type: 'CURRENCY_PERCENT';
}

interface ISBSCalculatorPerkListColumn {
  config: IPerkListColumnProps;
  type: 'PERK_LIST';
}

type ISBSCalculatorColumnData =
  | ISBSCalculatorTextColumn
  | ISBSCalculatorHeaderColumn
  | ISBSCalculatorCurrencyColumn
  | ISBSCalculatorCurrencyPercentColumn
  | ISBSCalculatorPerkListColumn;

interface ISBSCalculatorColumn {
  [index: string]: ISBSCalculatorColumnData;
}

interface ISBSCalculatorBaseField extends ISideBySideBaseField {
  tooltip?: React.ReactNode;
}

type ISBSCalculatorSubField = ISideBySideSubField & ISBSCalculatorEditable;

type ISBSCalculatorField = ISBSCalculatorBaseField | ISBSCalculatorSubField;

export interface ISBSCalculatorProps {
  calculatorState: Map<string, any> | {};
  columns: ISBSCalculatorColumn[];
  fields: ISBSCalculatorField[];
  onUpdate: (key, value) => void;
}

const getCalculatorFields = (fields, calculatorState, onUpdate): ISideBySideField[] =>
  fields.map(field => {
    switch (field.type) {
      case 'SUBFIELD':
        const showSubFields = field.isEditable
          ? calculatorState[field.onUpdateKey]
          : field.showSubFields;

        return {
          ...field,
          label: (
            <SubfieldLabel
              label={field.label}
              onClick={onUpdate.bind(null, field.onUpdateKey, !showSubFields)}
              showSubFields={showSubFields}
            />
          ),
          showSubFields,
          subFields: getCalculatorFields(field.subFields, calculatorState, onUpdate),
        };
      default:
        return {
          ...field,
          label: field.label && <FieldLabel label={field.label} tooltip={field.tooltip} />,
        };
    }
  });

const getCalculatorColumns = (columns, calculatorState, onUpdate): ISideBySideColumn[] =>
  columns.map((column, columnIndex) =>
    mapValues(column, (columnData: ISBSCalculatorColumnData, key) => {
      switch (columnData.type) {
        case 'TEXT':
          return {
            cell: <TextColumn {...columnData.config} />,
          };
        case 'HEADER':
          return {
            cell: <HeaderColumn {...columnData.config} />,
          };
        case 'CURRENCY':
          if (columnData.isEditable) {
            const onUpdateKey = columnData.onUpdateKey;

            return {
              cell: (
                <CurrencyInput
                  className={'od-sbsc__input'}
                  content={calculatorState[onUpdateKey]}
                  name={`${columnIndex}-${key}`}
                  onChange={(name, value) => onUpdate(onUpdateKey, value)}
                  placeholder={'$0'}
                  {...columnData.config}
                />
              ),
            };
          } else {
            const currencyString = centsToCurrencyString(columnData.config['value']);
            return {
              cell: columnData.config['bolder'] ? (
                <h3 className="od-sbsc__bolder">{currencyString}</h3>
              ) : (
                currencyString
              ),
            };
          }
        case 'CURRENCY_PERCENT':
          return {
            cell: (
              <PercentCurrencyInput
                checkboxChecked={!!columnData.config.checkboxChecked}
                checkboxExists={!!columnData.config.checkboxExists}
                className="od-sbsc__input"
                disabled={!columnData.isEditable}
                name={`${columnIndex}-${key}`}
                onChange={(name, value) => onUpdate(columnData.onUpdateKey, value)}
                {...columnData.config}
                {...calculatorState[columnData.onUpdateKey]}
              />
            ),
          };
        case 'PERK_LIST':
          return {
            cell: <PerkListColumn {...columnData.config} />,
          };
        default:
          return columnData;
      }
    }),
  );

const SideBySideCalculator: React.SFC<ISBSCalculatorProps> = (props: ISBSCalculatorProps) => {
  const { calculatorState, columns, fields, onUpdate } = props;

  return (
    <div className="od-sbsc">
      <SideBySide
        columns={getCalculatorColumns(columns, calculatorState, onUpdate)}
        fields={getCalculatorFields(fields, calculatorState, onUpdate)}
      />
    </div>
  );
};

export default SideBySideCalculator;
